/**
 * In-Memory Rate Limiter
 * 
 * This module provides client-side rate limiting to prevent abuse and
 * reduce unnecessary API calls. It implements both sliding window and
 * burst protection algorithms.
 * 
 * Features:
 * - Per-endpoint rate limiting
 * - Sliding window algorithm
 * - Burst protection
 * - Memory-efficient cleanup
 * - Configurable limits
 */

/**
 * Rate Limiter class that tracks requests per endpoint
 */
export class RateLimiter {
  constructor(config = {}) {
    // Configuration with defaults
    this.config = {
      maxRequests: config.maxRequests || 100,        // Max requests per window
      windowMs: config.windowMs || 60000,            // Time window in milliseconds (1 minute)
      burstLimit: config.burstLimit || 20,           // Max burst requests
      burstWindowMs: config.burstWindowMs || 10000,  // Burst window in milliseconds (10 seconds)
      cleanupInterval: config.cleanupInterval || 300000, // Cleanup interval (5 minutes)
      maxEndpoints: config.maxEndpoints || 1000      // Max endpoints to track (memory limit)
    };

    // Storage for request tracking
    this.requests = new Map(); // endpoint -> array of timestamps
    this.burstRequests = new Map(); // endpoint -> array of timestamps for burst tracking
    this.endpointOrder = []; // LRU tracking for memory management

    // Start cleanup timer
    this.startCleanupTimer();
  }

  /**
   * Check if a request is allowed for the given endpoint
   * @param {string} endpoint - The API endpoint being accessed
   * @returns {object} - { allowed: boolean, remaining: number, retryAfter: number }
   */
  checkLimit(endpoint) {
    const now = Date.now();
    
    // Clean up old requests first
    this.cleanupEndpoint(endpoint, now);
    
    // Get current requests for this endpoint
    const requests = this.requests.get(endpoint) || [];
    const burstRequests = this.burstRequests.get(endpoint) || [];
    
    // Check burst limit first (more restrictive)
    const recentBurstRequests = burstRequests.filter(
      timestamp => now - timestamp < this.config.burstWindowMs
    );
    
    if (recentBurstRequests.length >= this.config.burstLimit) {
      const oldestBurstRequest = Math.min(...recentBurstRequests);
      const retryAfter = Math.ceil((oldestBurstRequest + this.config.burstWindowMs - now) / 1000);
      
      return {
        allowed: false,
        remaining: 0,
        retryAfter: retryAfter,
        reason: 'burst_limit_exceeded',
        limit: this.config.burstLimit,
        window: this.config.burstWindowMs
      };
    }
    
    // Check main window limit
    const recentRequests = requests.filter(
      timestamp => now - timestamp < this.config.windowMs
    );
    
    if (recentRequests.length >= this.config.maxRequests) {
      const oldestRequest = Math.min(...recentRequests);
      const retryAfter = Math.ceil((oldestRequest + this.config.windowMs - now) / 1000);
      
      return {
        allowed: false,
        remaining: 0,
        retryAfter: retryAfter,
        reason: 'rate_limit_exceeded',
        limit: this.config.maxRequests,
        window: this.config.windowMs
      };
    }
    
    // Request is allowed - record it
    this.recordRequest(endpoint, now);
    
    return {
      allowed: true,
      remaining: this.config.maxRequests - recentRequests.length - 1,
      retryAfter: 0,
      reason: null,
      limit: this.config.maxRequests,
      window: this.config.windowMs
    };
  }

  /**
   * Record a request for the given endpoint
   * @param {string} endpoint - The API endpoint
   * @param {number} timestamp - The request timestamp
   */
  recordRequest(endpoint, timestamp) {
    // Update LRU order
    this.updateLRU(endpoint);
    
    // Add to main requests tracking
    if (!this.requests.has(endpoint)) {
      this.requests.set(endpoint, []);
    }
    this.requests.get(endpoint).push(timestamp);
    
    // Add to burst requests tracking
    if (!this.burstRequests.has(endpoint)) {
      this.burstRequests.set(endpoint, []);
    }
    this.burstRequests.get(endpoint).push(timestamp);
    
    // Enforce memory limits
    this.enforceMemoryLimits();
  }

  /**
   * Update LRU order for memory management
   * @param {string} endpoint - The endpoint being accessed
   */
  updateLRU(endpoint) {
    // Remove from current position
    const index = this.endpointOrder.indexOf(endpoint);
    if (index > -1) {
      this.endpointOrder.splice(index, 1);
    }
    
    // Add to end (most recently used)
    this.endpointOrder.push(endpoint);
  }

  /**
   * Enforce memory limits by removing least recently used endpoints
   */
  enforceMemoryLimits() {
    while (this.endpointOrder.length > this.config.maxEndpoints) {
      const oldestEndpoint = this.endpointOrder.shift();
      this.requests.delete(oldestEndpoint);
      this.burstRequests.delete(oldestEndpoint);
    }
  }

  /**
   * Clean up old requests for a specific endpoint
   * @param {string} endpoint - The endpoint to clean up
   * @param {number} now - Current timestamp
   */
  cleanupEndpoint(endpoint, now) {
    // Clean up main requests
    if (this.requests.has(endpoint)) {
      const requests = this.requests.get(endpoint);
      const validRequests = requests.filter(
        timestamp => now - timestamp < this.config.windowMs
      );
      
      if (validRequests.length === 0) {
        this.requests.delete(endpoint);
      } else {
        this.requests.set(endpoint, validRequests);
      }
    }
    
    // Clean up burst requests
    if (this.burstRequests.has(endpoint)) {
      const burstRequests = this.burstRequests.get(endpoint);
      const validBurstRequests = burstRequests.filter(
        timestamp => now - timestamp < this.config.burstWindowMs
      );
      
      if (validBurstRequests.length === 0) {
        this.burstRequests.delete(endpoint);
      } else {
        this.burstRequests.set(endpoint, validBurstRequests);
      }
    }
  }

  /**
   * Get current status for an endpoint
   * @param {string} endpoint - The endpoint to check
   * @returns {object} - Status information
   */
  getStatus(endpoint) {
    const now = Date.now();
    this.cleanupEndpoint(endpoint, now);
    
    const requests = this.requests.get(endpoint) || [];
    const burstRequests = this.burstRequests.get(endpoint) || [];
    
    const recentRequests = requests.filter(
      timestamp => now - timestamp < this.config.windowMs
    );
    
    const recentBurstRequests = burstRequests.filter(
      timestamp => now - timestamp < this.config.burstWindowMs
    );
    
    return {
      endpoint,
      requests: recentRequests.length,
      maxRequests: this.config.maxRequests,
      burstRequests: recentBurstRequests.length,
      maxBurstRequests: this.config.burstLimit,
      windowMs: this.config.windowMs,
      burstWindowMs: this.config.burstWindowMs,
      remaining: Math.max(0, this.config.maxRequests - recentRequests.length),
      burstRemaining: Math.max(0, this.config.burstLimit - recentBurstRequests.length)
    };
  }

  /**
   * Reset rate limit for a specific endpoint
   * @param {string} endpoint - The endpoint to reset
   */
  reset(endpoint) {
    this.requests.delete(endpoint);
    this.burstRequests.delete(endpoint);
    
    // Remove from LRU order
    const index = this.endpointOrder.indexOf(endpoint);
    if (index > -1) {
      this.endpointOrder.splice(index, 1);
    }
  }

  /**
   * Reset all rate limits
   */
  resetAll() {
    this.requests.clear();
    this.burstRequests.clear();
    this.endpointOrder.length = 0;
  }

  /**
   * Get statistics about the rate limiter
   * @returns {object} - Statistics
   */
  getStats() {
    return {
      totalEndpoints: this.requests.size,
      maxEndpoints: this.config.maxEndpoints,
      config: { ...this.config },
      memoryUsage: {
        endpoints: this.endpointOrder.length,
        requestMaps: this.requests.size,
        burstMaps: this.burstRequests.size
      }
    };
  }

  /**
   * Start the cleanup timer to periodically clean old data
   */
  startCleanupTimer() {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }
    
    this.cleanupTimer = setInterval(() => {
      this.performGlobalCleanup();
    }, this.config.cleanupInterval);
  }

  /**
   * Perform global cleanup of old requests
   */
  performGlobalCleanup() {
    const now = Date.now();
    const endpointsToRemove = [];
    
    // Clean up all endpoints
    for (const endpoint of this.requests.keys()) {
      this.cleanupEndpoint(endpoint, now);
      
      // If endpoint has no recent requests, mark for removal
      if (!this.requests.has(endpoint) && !this.burstRequests.has(endpoint)) {
        endpointsToRemove.push(endpoint);
      }
    }
    
    // Remove empty endpoints from LRU order
    endpointsToRemove.forEach(endpoint => {
      const index = this.endpointOrder.indexOf(endpoint);
      if (index > -1) {
        this.endpointOrder.splice(index, 1);
      }
    });
    
    // Log cleanup stats in development
    if (process.env.NODE_ENV === 'development' && endpointsToRemove.length > 0) {
      console.log(`Rate Limiter: Cleaned up ${endpointsToRemove.length} inactive endpoints`);
    }
  }

  /**
   * Stop the cleanup timer
   */
  stopCleanupTimer() {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  /**
   * Destroy the rate limiter and clean up resources
   */
  destroy() {
    this.stopCleanupTimer();
    this.resetAll();
  }
}

/**
 * Create a rate limiter with common presets
 */
export const createRateLimiter = {
  /**
   * Conservative rate limiter for production use
   */
  conservative: () => new RateLimiter({
    maxRequests: 60,      // 1 request per second on average
    windowMs: 60000,      // 1 minute window
    burstLimit: 10,       // Allow small bursts
    burstWindowMs: 10000  // 10 second burst window
  }),

  /**
   * Moderate rate limiter for typical applications
   */
  moderate: () => new RateLimiter({
    maxRequests: 100,     // ~1.67 requests per second on average
    windowMs: 60000,      // 1 minute window
    burstLimit: 20,       // Allow moderate bursts
    burstWindowMs: 10000  // 10 second burst window
  }),

  /**
   * Aggressive rate limiter for high-traffic applications
   */
  aggressive: () => new RateLimiter({
    maxRequests: 300,     // 5 requests per second on average
    windowMs: 60000,      // 1 minute window
    burstLimit: 50,       // Allow larger bursts
    burstWindowMs: 10000  // 10 second burst window
  }),

  /**
   * Development rate limiter with relaxed limits
   */
  development: () => new RateLimiter({
    maxRequests: 1000,    // Very high limit for development
    windowMs: 60000,      // 1 minute window
    burstLimit: 100,      // High burst limit
    burstWindowMs: 10000  // 10 second burst window
  })
};

export default RateLimiter;
